import React, { useState } from "react";
import slugify from "slugify";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import { graphql, Link, navigate } from "gatsby";
import Layout from "../components/layout";
import {
  BuildingIcon,
  CircleTickIcon,
  PhoneChatIcon,
} from "../components/Icons";
import Seo from "../components/seo";
import FaqAccordion from "../components/FaqAccordion";
import {
  Section,
  SectionPageTitle,
  Container,
  SectionTitle,
  SectionDescription,
  MarkdownContent
} from "../components/Section";
import DefaultButton from "../components/Button/DefaultButton";
import PrimaryButton from "../components/Button/PrimaryButton";
import {
  TopHeaderGrid,
  TopLeftHeader,
  TopRightHeader,
  StateHero,
  StateBanner,
  StateMap,
  PopularSize,
  PopularAction,
  SizeTitle,
  ListSize,
  ListItemSize,
  ListCity,
  ListCityAction,
  BuildGrid,
  BuildLeftGrid,
  BuildRightGrid,
  HeroBackground,
  Service,
  ServiceLeftItem,
  ServiceRightItem,
  StyledWindowedSelect,
} from "../components/stateTemplate";
import { FaqGrid, FaqLeftGrid, FaqRightGrid } from "../components/Section";
import ProductSection from "../sections/Home/ProductSection";
import { MapIcon, CallIcon } from "../components/Icons"
import styled from "styled-components";

const AddressInfo = styled.div`
  position: relative;
  display: flex;
  flex-wrap:wrap;
  padding:15px 0;
  margin: 0 auto;
  max-width: 320px;
  width:100%;
  flex-direction:column;
  @media(min-width:576px){
    justify-content: center;
    max-width:none;
    margin:0 -5px;
    flex-direction:row;
  }
`
const AddressItem = styled.div`
  display:flex;
  flex-wrap:nowrap;
  color:#373636;    
  font-size:16px;
  line-height:26px;
  justify-content:flex-start;
  @media(min-width:576px){
    padding:0 5px;
    justify-content:center;
    font-size:18px;
    line-height:30px;
  }
  a{
    display:inline-block;
    color:#373636;   
    &:hover{
      color:#EA222B;
    }
  }
  + .add-item{    
    margin-top:5px;
    @media(min-width:576px){
      margin-left:10px;
      margin-top:0;
    }
  }
  .icon{
    display:flex;
    align-items:center;
    justify-content:center;
    height:28px;
    @media(min-width:1600px){
      height:30px;
    }
    > svg{
      fill:#EA222B;
      width:18px;
      @media(min-width:576px){
        width:22px;
      }
    }
    + .text{
      margin-left:10px;
    }
  }
`

const StateTemplate = ({ location, data }) => {
  const [limit, setlimit] = useState(30);
  const STATE_IDAHO = "idaho";
  const {
    contentfulStatePages: stateData,
    allContentfulStatePages: { edges: allStates },
  } = data;
  const popularLocations = allStates.reduce((pLocations, state) => {
    state.node.popularCities &&
      state.node.popularCities.forEach((popularCity) => {
        pLocations.push({
          label: popularCity,
          value: state.node.stateName.split(" ").join("-").toLowerCase(),
        });
      });

    return [...pLocations];
  }, []);

  const breadcrumbSchema = {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home Page",
        item: "https://www.engineeredmetalbuildings.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: stateData.stateName,
        item: `https://www.engineeredmetalbuildings.com/${stateData.stateName}/`,
      },
    ],
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "image": "https://www.engineeredmetalbuildings.com/static/b81f2bb5f87a3992879586deaa81d02f/88ed0/logo.webp",
    "url": "https://www.engineeredmetalbuildings.com/",
    "sameAs": [
      "https://maps.app.goo.gl/yabxaKrf3LRjxD9F8",
      "https://www.embmetalbuildings.com",
      "https://www.engineeredmetalbuildings.com/idaho/",
      "https://www.bbb.org/us/id/heyburn/profile/metal-building-contractors/emb-metal-buildings-llc-idaho-1296-1000182247"
    ],
    "logo": "https://www.engineeredmetalbuildings.com/static/b81f2bb5f87a3992879586deaa81d02f/88ed0/logo.webp",
    "name": "EMB Metal Buildings, LLC",
    "description": "At EMB Metal Buildings, we provide a wide range of strong, durable, and affordable structures to meet all your storage needs. Order your building today!",
    "email": "sales@embmetalbuildings.com",
    "telephone": "(208) 572-1441",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "340 Centennial Drive",
      "addressLocality": "Heyburn",
      "addressRegion": "ID",
      "postalCode": "83336",
      "addressCountry": "US"
    }
  };

  const schema = [breadcrumbSchema, organizationSchema];

  if (stateData.frequentlyAskedQuestions) {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": stateData.frequentlyAskedQuestions.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": (faq.answer.childMarkdownRemark.html || "").replace(
            /<[^>]+>/g,
            ""
          )
        }
      }))
    }

    schema.push(faqSchema);
  }

  return (
    <Layout location={location}>
      <Seo
        title={stateData.metaTitle}
        description={stateData.metaDescription}
        schemaMarkup={schema}
      />
      <Section
        ept="160px"
        epb="60px"
        xpt="140px"
        xpb="30px"
        pt="100px"
        pb="15px"
        bgColor="#fff"
        className="section-state"
      >
        <Container>
          <SectionPageTitle textAlign="center" color="#00236A" mb="0">
            {`Metal Buildings ${stateData.stateName}`}
          </SectionPageTitle>
          {stateData.stateName?.toLowerCase() === STATE_IDAHO && 
            <AddressInfo className="add-info">
              <AddressItem className="add-item">
                <span className="icon"><MapIcon /></span>
                <a className="text" href="https://maps.app.goo.gl/u5fHULcqkv4MPDzC6" target="_blank">340 Centennial Dr, Heyburn, ID 83336 </a>              
              </AddressItem>
              <AddressItem className="add-item">
                <span className="icon"><CallIcon /></span>
                <a className="text" href="tel:(208) 261-4858" aria-label="phone number">(208) 261-4858</a>
              </AddressItem>
            </AddressInfo>
          }
          <TopHeaderGrid>
            <TopLeftHeader>
              <PopularSize>
                <SizeTitle>{`Popular Sizes in ${stateData.stateName}`}</SizeTitle>
                <ListSize>
                  {stateData.metalBuildings.map((building, index) => (
                    <ListItemSize className="list-item" key={index}>
                      <Link to={building.url}>
                        <span className="icon" key={index}>
                          <BuildingIcon />
                        </span>
                        <span className="text">{building.title}</span>
                      </Link>
                    </ListItemSize>
                  ))}
                </ListSize>
              </PopularSize>
              <PopularAction>
                <a href="tel:2085721441" aria-label="phone number">
                  <PrimaryButton
                    icon={<PhoneChatIcon />}
                    text="(208) 572-1441"
                    size="xl"
                  />
                </a>
              </PopularAction>
            </TopLeftHeader>
            <TopRightHeader>
              <StateHero>
                <StateBanner>
                  <GatsbyImage
                    image={stateData.metalBuildingsImage.gatsbyImageData}
                    alt="Metal Building Image"
                  />
                </StateBanner>
                <StateMap>
                  <GatsbyImage
                    image={stateData.stateLocationPin.gatsbyImageData}
                    alt={stateData.stateName}
                  />
                </StateMap>
              </StateHero>
            </TopRightHeader>
          </TopHeaderGrid>
        </Container>
      </Section>

      <ProductSection data={data.allContentfulProduct.edges} />

      <Section
        ept="60px"
        epb="60px"
        xpt="40px"
        xpb="40px"
        pt="30px"
        pb="20px"
        bgColor="#fff"
        className="section-certify"
        zIndex="0"
      >
        <BuildGrid>
          <BuildLeftGrid>
            <HeroBackground>
              <GatsbyImage
                image={stateData.certifiedSectionImage.gatsbyImageData}
                alt={`Certified ${stateData.stateName} Metal Buildings`}
              />
            </HeroBackground>
          </BuildLeftGrid>
          <BuildRightGrid>
            <SectionTitle>{stateData.certifiedSectionTitle}</SectionTitle>
            <SectionDescription
              mb="0"
              dangerouslySetInnerHTML={{
                __html:
                  stateData.certifiedSectionDescription.childMarkdownRemark
                    .html,
              }}
            />
          </BuildRightGrid>
        </BuildGrid>
      </Section>

      {stateData.secondContent?.childMarkdownRemark?.html && (
        <Section
          ept="60px"
          epb="60px"
          xpt="40px"
          xpb="40px"
          pt="30px"
          pb="20px"
          bgColor="#eef0fd"
          className="section-content"
        >
          <Container>
            <MarkdownContent
              dangerouslySetInnerHTML={{ __html: stateData.secondContent.childMarkdownRemark.html }}
            />
          </Container>
        </Section>
      )}

      <Section
        ept="60px"
        epb="60px"
        xpt="40px"
        xpb="40px"
        pt="20px"
        pb="15px"
        bgColor="#fff"
        className="section-location"
      >
        <Container>
          <SectionTitle textAlign="center">
            {`Frequently Asked Questions in ${stateData.stateName}`}
          </SectionTitle>
          <FaqGrid>
            <FaqLeftGrid>
              <FaqAccordion faqs={stateData.frequentlyAskedQuestions} />
            </FaqLeftGrid>
            <FaqRightGrid>
              <StaticImage src="../images/faq-charator.png" alt="Faq" />
            </FaqRightGrid>
          </FaqGrid>
        </Container>
      </Section>
      <Section
        ept="60px"
        epb="60px"
        xpt="40px"
        xpb="40px"
        pt="15px"
        pb="30px"
        bgColor="#eef0fd"
        className="section-city"
      >
        <Container>
          <SectionTitle textAlign="center">
            {`Popular Delivery Locations in ${stateData.stateName}`}            
          </SectionTitle>
          <SectionDescription textAlign="center">
            <p>
              {`Our installers deliver metal buildings near you all over Western, Central, and Eastern ${stateData.stateName}, including:`}
            </p>
          </SectionDescription>
          <ListCity>
            {stateData.popularDeliveryLocations
              .slice(0, limit)
              .map((locationName, index) => (
                <Link to={`/${slugify(stateData.stateName.toLowerCase())}/${slugify(locationName.content.toLowerCase())}`} key={index} className="list-item">
                  <span className="icon" key={index}>
                    <CircleTickIcon />
                  </span>
                  <span className="text">{locationName.content}</span>
                </Link>
              ))}
          </ListCity>
          {limit < stateData.popularDeliveryLocations.length && (
            <ListCityAction>
              <DefaultButton
                text="Load More"
                size="xl"
                onClick={() => setlimit(limit + 30)}
              />
            </ListCityAction>
          )}
        </Container>
      </Section>
      <Section
        ept="60px"
        epb="60px"
        xpt="40px"
        xpb="40px"
        pt="15px"
        pb="30px"
        bgColor="#fff"
        className="section-service"
        zIndex={2}
      >
        <Container>
          <Service>
            <ServiceLeftItem>
              <SectionTitle>Popular Service Locations</SectionTitle>
              <SectionDescription>
                <p>
                  We proudly offer delivery and installation of metal buildings
                  throughout various regions in the U.S. Some of the top areas
                  we serve include:
                </p>
              </SectionDescription>
            </ServiceLeftItem>
            <ServiceRightItem>
              {typeof window !== `undefined` && (
                <StyledWindowedSelect
                  options={popularLocations}
                  onChange={(e) => {
                    if (e.value) navigate(`/${e.value}`);
                  }}
                  placeholder="Browse Popular Locations..."
                />
              )}
            </ServiceRightItem>
          </Service>
        </Container>
      </Section>
    </Layout>
  );
};

export default StateTemplate;

export const pageQuery = graphql`
  query StateTemplateQuery($id: String!) {
    contentfulStatePages(id: { eq: $id }) {
      metaTitle
      metaDescription
      stateName      
      metalBuildings {
        title
        url
      }
      metalBuildingsImage {
        gatsbyImageData(quality: 50, placeholder: BLURRED)
        fluid {
          srcWebp
        }
      }
      stateLocationPin {
        gatsbyImageData(quality: 50, placeholder: BLURRED)
        fluid {
          srcWebp
        }
      }
      popularDeliveryLocations {
        content
      }
      frequentlyAskedQuestions {
        question
        answer {
          childMarkdownRemark {
            html
          }
        }
      }
      certifiedSectionTitle
      certifiedSectionDescription {
        childMarkdownRemark {
          html
        }
      }      
      certifiedSectionImage {
        gatsbyImageData(quality: 50, placeholder: BLURRED)
        fluid {
          srcWebp
        }
      }
      secondContent {
        childMarkdownRemark {
          html
        }
      }
    }
    allContentfulStatePages(filter: { id: { ne: $id } }) {
      edges {
        node {
          stateName
          popularCities
        }
      }
    }
    allContentfulProduct(
      sort: { fields: skuNumber, order: ASC }
      limit: 24
      filter: {isPopular: {eq: true}}
    ) {
      edges {
        node {
          id
          url
          rto
          financingAvailable
          skuNumber
          title
          image {
            gatsbyImageData(quality: 50, placeholder: BLURRED)
            fluid {
              srcWebp
            }
          }
          price
          width
          length
          height
          city
          state
          zipcode
          location {
            lat
            lon
          }
          buildingCondition
          description {
            childMarkdownRemark {
              html
            }
          }
          specifications {
            childMarkdownRemark {
              html
            }
          }
          productCategories {
            title
          }
          buildingTags
          roofStyleType
          manufacturer
          gaugeTubing
          gaugePanels
        }
      }
    }
  }
`;